import { WizardContact } from '../../providers/api/dtos';

export interface GroupedValidationErrors {
  contactErrors: {
    contactIndex: number,
    contactName: string,
    errors: string[],
  }[],
  otherErrors: string[],
}

/**
 * Groups validation errors by contact.
 * Errors with paths like `contacts[0].overriddenFields.fiscalCode` are grouped
 * by contact index, while other errors are kept separate.
 *
 * @param additionalInfo - The validation errors object with paths as keys and messages as values
 * @param contacts - The contacts array to get contact names from
 * @param pathPrefix - The prefix used in error paths (default: 'contacts')
 */
export function groupValidationErrorsByContact(
  additionalInfo: Record<string, string>,
  contacts?: WizardContact[],
  pathPrefix: string = 'contacts',
): GroupedValidationErrors {
  const contactPathRegex = new RegExp(`^${pathPrefix}\\[(\\d+)\\]`);
  const contactErrorsMap = new Map<number, string[]>();
  const otherErrors: string[] = [];

  Object.entries(additionalInfo).forEach(([path, message]) => {
    const match = path.match(contactPathRegex);

    if (match) {
      const contactIndex = parseInt(match[1], 10);
      const existingErrors = contactErrorsMap.get(contactIndex) || [];
      contactErrorsMap.set(contactIndex, [...existingErrors, message]);
    } else {
      otherErrors.push(message);
    }
  });

  const contactErrors = Array.from(contactErrorsMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([contactIndex, errors]) => {
      const contact = contacts?.[contactIndex];
      const contactName = contact?.overriddenFields?.name
        || contact?.name
        || `Contatto ${contactIndex + 1}`;

      return {
        contactIndex,
        contactName,
        errors,
      };
    });

  return {
    contactErrors,
    otherErrors,
  };
}
